import {
  type FormEvent,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { type Api, ApiError } from "../api";

type AppliedChanges = { texts: number; themeColors: number };

type ChatTurn = {
  id: number;
  role: "user" | "assistant";
  message: string;
  applied?: AppliedChanges;
};

/**
 * UX-2 — the conversation half of the Lovable-style Website view. Every
 * applied edit calls onApplied() so the preview pane reloads immediately
 * (the K5 overlay is read per-request — no rebuild, no cache wait).
 * When the API says this site isn't chat-editable yet (mirror/imported
 * lane), the rail switches to an honest explainer instead of a dead chat.
 */
export function ChatRail({
  api,
  onApplied,
  onOpenEditor,
}: {
  api: Api;
  onApplied: () => void;
  onOpenEditor: () => void;
}) {
  const [draft, setDraft] = useState("");
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [busy, setBusy] = useState(false);
  const [unsupported, setUnsupported] = useState(false);
  const logRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, []);

  const submit = async () => {
    const message = draft.trim();
    if (!message || busy) return;
    const userTurn: ChatTurn = { id: Date.now(), role: "user", message };
    setTurns((current) => [...current, userTurn]);
    setDraft("");
    setBusy(true);
    try {
      const result = await api.chatEdit(message);
      const appliedCount = result.applied.texts + result.applied.themeColors;
      setTurns((current) => [
        ...current,
        {
          id: userTurn.id + 1,
          role: "assistant",
          message: result.reply,
          applied: result.applied,
        },
      ]);
      if (appliedCount > 0) onApplied();
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.status === 409 &&
        /generate a site first|no composition/i.test(error.message)
      ) {
        setUnsupported(true);
      } else {
        setTurns((current) => [
          ...current,
          {
            id: userTurn.id + 1,
            role: "assistant",
            message:
              error instanceof ApiError
                ? error.message
                : "Something went wrong — try again.",
          },
        ]);
      }
    } finally {
      setBusy(false);
      requestAnimationFrame(() =>
        logRef.current?.scrollTo({
          top: logRef.current.scrollHeight,
          behavior: "smooth",
        }),
      );
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submit();
  };
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void submit();
    }
  };

  if (unsupported) {
    return (
      <aside className="chat-rail">
        <div className="chat-rail-head">
          <h2>Edit by chat</h2>
        </div>
        <div className="chat-rail-empty">
          <p>
            Chat editing isn't available for imported sites yet — it arrives
            with the next generation update.
          </p>
          <p className="muted">
            You can still change any text or image directly:
          </p>
          <button type="button" onClick={onOpenEditor}>
            Open the visual editor
          </button>
        </div>
      </aside>
    );
  }

  return (
    <aside className="chat-rail">
      <div className="chat-rail-head">
        <h2>Edit by chat</h2>
        <p className="muted small">Changes apply to the preview instantly.</p>
      </div>
      <div className="chat-rail-log" ref={logRef} aria-live="polite">
        {turns.length === 0 && (
          <div className="chat-rail-hint">
            <p>Tell Groundbeam what to change — for example:</p>
            <ul>
              <li>"Make the headline mention same-day service"</li>
              <li>"Use a deeper green for buttons"</li>
              <li>"Rewrite the about section to sound friendlier"</li>
            </ul>
          </div>
        )}
        {turns.map((turn) => {
          const appliedCount = turn.applied
            ? turn.applied.texts + turn.applied.themeColors
            : 0;
          return (
            <div className={`chat-bubble ${turn.role}`} key={turn.id}>
              <span>{turn.message}</span>
              {appliedCount > 0 && (
                <small className="chat-bubble-applied">
                  ✓ {appliedCount} change{appliedCount === 1 ? "" : "s"} applied
                  — preview updated
                </small>
              )}
            </div>
          );
        })}
        {busy && (
          <div className="chat-bubble assistant chat-bubble-busy" role="status">
            <span className="source-runner-pulse" aria-hidden="true" />
            <span>Working on it…</span>
          </div>
        )}
      </div>
      <form className="chat-rail-form" onSubmit={onSubmit}>
        <textarea
          aria-label="Describe the site changes"
          disabled={busy}
          maxLength={4000}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Describe a change to your website…"
          rows={2}
          value={draft}
        />
        <button disabled={busy || !draft.trim()} type="submit">
          Send
        </button>
      </form>
    </aside>
  );
}
